import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Legend from './Legend';
import YearSlider from './YearSlider';
import { VIEWS } from './MigrationMap';
import type { ViewName } from './MigrationMap';
import { compactNumber } from '../format';
import { MIGRATION_COLORS } from '../theme';
import type { MigrationManifest, YearStats } from '../types';

export const PANEL_WIDTH = { xs: 248, sm: 288, md: 320 };

interface SidePanelProps {
  manifest: MigrationManifest;
  year: number;
  onYearChange: (year: number) => void;
  view: ViewName;
  onViewChange: (view: ViewName) => void;
  stats?: YearStats;
}

/**
 * Nationally, county gains and losses cancel out -- every domestic move is one
 * county's gain and another's loss -- so a "total in" and "total out" pair is
 * just the same number twice. One figure is the honest summary: the volume of
 * people who ended up in a different county than they started in.
 */
function NetVolumeTile({ stats, peoplePerDot }: { stats: YearStats; peoplePerDot: number }) {
  const volume = Math.round((stats.inflow + Math.abs(stats.outflow)) / 2);
  const dots = stats.positiveDots + stats.negativeDots;

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'rgba(28,110,164,0.04)',
      }}
    >
      <Typography
        variant="h2"
        sx={{
          color: MIGRATION_COLORS.accent,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.15,
        }}
      >
        {compactNumber(volume)}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
        net county-to-county relocation
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.68rem',
          display: 'block',
          mt: 0.5,
        }}
      >
        Gains and losses offset nationally, so this is the volume that shifted — drawn as{' '}
        {dots.toLocaleString()} dots at {peoplePerDot} people each.
      </Typography>
    </Box>
  );
}

export default function SidePanel({
  manifest,
  year,
  onYearChange,
  view,
  onViewChange,
  stats,
}: SidePanelProps) {
  return (
    <Box
      component="aside"
      sx={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        height: '100%',
        overflowY: 'auto',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} sx={{ p: 2.5, flexGrow: 1 }}>
        {/* Title */}
        <Box>
          <Typography variant="h1" color="primary" sx={{ lineHeight: 1.2 }}>
            Net Domestic Migration
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            by U.S. county
          </Typography>
        </Box>

        <Divider />

        <YearSlider years={manifest.years} year={year} onChange={onYearChange} />

        {stats && <NetVolumeTile stats={stats} peoplePerDot={manifest.peoplePerDot} />}

        <Divider />

        <Box>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              letterSpacing: '0.08em',
              fontWeight: 700,
              display: 'block',
              mb: 0.75,
            }}
          >
            Region
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {(Object.keys(VIEWS) as ViewName[]).map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                clickable
                color={view === name ? 'primary' : 'default'}
                variant={view === name ? 'filled' : 'outlined'}
                onClick={() => onViewChange(name)}
              />
            ))}
          </Stack>
        </Box>

        <Divider />

        <Legend peoplePerDot={manifest.peoplePerDot} />
      </Stack>

      <Box sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.68rem',
            lineHeight: 1.45,
            display: 'block',
          }}
        >
          {manifest.countyCount.toLocaleString()} counties · Source: U.S. Census Bureau, Vintage{' '}
          {manifest.estimatesVintage} Population Estimates
        </Typography>
      </Box>
    </Box>
  );
}
